import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Activity, AlertTriangle, CheckCircle, XCircle, 
  TrendingUp, Clock, Server, Bell, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';
import { API_ENDPOINTS } from '../../config/api';

interface ProviderHealth {
  providerId: string;
  providerName: string;
  status: string;
  lastCheck: string;
  consecutiveFailures: number;
  responseTimeMs?: number;
  errorMessage?: string;
}

interface Alert {
  id: string;
  type: string;
  severity: string;
  title: string;
  message: string;
  createdAt: string;
  acknowledgedAt?: string;
}

export function Monitoring() {
  const [activeTab, setActiveTab] = useState<'health' | 'alerts' | 'metrics'>('health');

  const { data: healthSummary } = useQuery({
    queryKey: ['monitoring-health-summary'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.providerEngine.monitoring}/health`);
      return response.data;
    },
    refetchInterval: 30000,
  });

  const { data: providerHealth, refetch: refetchHealth } = useQuery({
    queryKey: ['monitoring-provider-health'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.providerEngine.monitoring}/health/providers`);
      return response.data;
    },
    refetchInterval: 30000,
  });

  const { data: alerts } = useQuery({
    queryKey: ['monitoring-alerts'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.providerEngine.monitoring}/alerts`);
      return response.data;
    },
    refetchInterval: 10000,
  });

  const { data: systemMetrics } = useQuery({
    queryKey: ['monitoring-system-metrics'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.providerEngine.monitoring}/metrics/system`);
      return response.data;
    },
    refetchInterval: 60000,
  });

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'HEALTHY': return 'text-green-400';
      case 'DEGRADED': return 'text-yellow-400';
      case 'UNHEALTHY': return 'text-red-400';
      default: return 'text-surface-400';
    }
  };

  const getHealthStatusIcon = (status: string) => {
    switch (status) {
      case 'HEALTHY': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'DEGRADED': return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'UNHEALTHY': return <XCircle className="w-5 h-5 text-red-400" />;
      default: return <Clock className="w-5 h-5 text-surface-400" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return <Badge variant="error">Crítico</Badge>;
      case 'ERROR': return <Badge variant="error">Erro</Badge>;
      case 'WARNING': return <Badge variant="warning">Aviso</Badge>;
      case 'INFO': return <Badge variant="info">Info</Badge>;
      default: return <Badge>{severity}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Monitoring</h1>
          <p className="text-surface-400">Monitore a saúde dos providers e alertas do sistema</p>
        </div>
        <Button variant="secondary" onClick={() => refetchHealth()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card variant="stat">
          <div className="flex items-center gap-2 text-surface-400 text-sm">
            <Server className="w-4 h-4" />
            Total Providers
          </div>
          <div className="text-2xl font-bold text-white">{healthSummary?.total || 0}</div>
        </Card>
        <Card variant="stat">
          <div className="flex items-center gap-2 text-surface-400 text-sm">
            <CheckCircle className="w-4 h-4 text-green-400" />
            Saudáveis
          </div>
          <div className="text-2xl font-bold text-green-400">{healthSummary?.healthy || 0}</div>
        </Card>
        <Card variant="stat">
          <div className="flex items-center gap-2 text-surface-400 text-sm">
            <AlertTriangle className="w-4 h-4 text-yellow-400" />
            Degradados
          </div>
          <div className="text-2xl font-bold text-yellow-400">{healthSummary?.degraded || 0}</div>
        </Card>
        <Card variant="stat">
          <div className="flex items-center gap-2 text-surface-400 text-sm">
            <XCircle className="w-4 h-4 text-red-400" />
            Com Problema
          </div>
          <div className="text-2xl font-bold text-red-400">{healthSummary?.unhealthy || 0}</div>
        </Card>
        <Card variant="stat">
          <div className="flex items-center gap-2 text-surface-400 text-sm">
            <Bell className="w-4 h-4 text-orange-400" />
            Alertas Ativos
          </div>
          <div className="text-2xl font-bold text-orange-400">{alerts?.length || 0}</div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-700">
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'health' 
              ? 'text-primary-400 border-b-2 border-primary-400' 
              : 'text-surface-400 hover:text-white'
          }`}
          onClick={() => setActiveTab('health')}
        >
          <Activity className="w-4 h-4 inline mr-2" />
          Health Status
        </button>
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'alerts' 
              ? 'text-primary-400 border-b-2 border-primary-400' 
              : 'text-surface-400 hover:text-white'
          }`}
          onClick={() => setActiveTab('alerts')}
        >
          <Bell className="w-4 h-4 inline mr-2" />
          Alertas ({alerts?.length || 0})
        </button>
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'metrics' 
              ? 'text-primary-400 border-b-2 border-primary-400' 
              : 'text-surface-400 hover:text-white'
          }`}
          onClick={() => setActiveTab('metrics')}
        >
          <TrendingUp className="w-4 h-4 inline mr-2" />
          Métricas
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'health' && (
        <Card>
          <h3 className="text-lg font-medium text-white mb-4">Status dos Providers</h3>
          {!providerHealth?.length ? (
            <div className="text-center py-8 text-surface-400">
              Nenhum provider monitorado
            </div>
          ) : (
            <div className="space-y-3">
              {providerHealth.map((health: ProviderHealth) => (
                <div
                  key={health.providerId}
                  className="flex items-center justify-between p-4 bg-surface-800/50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    {getHealthStatusIcon(health.status)}
                    <div>
                      <h4 className="font-medium text-white">{health.providerName}</h4>
                      <p className="text-sm text-surface-400">
                        Último check: {new Date(health.lastCheck).toLocaleString()}
                      </p>
                      {health.errorMessage && (
                        <p className="text-xs text-red-400 mt-1">{health.errorMessage}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {health.responseTimeMs && (
                      <span className="text-sm text-surface-400">
                        {health.responseTimeMs}ms
                      </span>
                    )}
                    <span className={`font-medium ${getHealthStatusColor(health.status)}`}>
                      {health.status}
                    </span>
                    {health.consecutiveFailures > 0 && (
                      <Badge variant="error">{health.consecutiveFailures} falhas</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {activeTab === 'alerts' && (
        <Card>
          <h3 className="text-lg font-medium text-white mb-4">Alertas Ativos</h3>
          {!alerts?.length ? (
            <div className="text-center py-8 text-surface-400">
              Nenhum alerta ativo
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert: Alert) => (
                <div
                  key={alert.id}
                  className="flex items-center justify-between p-4 bg-surface-800/50 rounded-lg border-l-4 border-red-500"
                >
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {getSeverityBadge(alert.severity)}
                      <span className="text-xs text-surface-500">{alert.type}</span>
                    </div>
                    <h4 className="font-medium text-white">{alert.title}</h4>
                    <p className="text-sm text-surface-400">{alert.message}</p>
                    <p className="text-xs text-surface-500 mt-1">
                      {new Date(alert.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm">Reconhecer</Button>
                    <Button variant="ghost" size="sm">Resolver</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {activeTab === 'metrics' && (
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-medium text-white mb-4">Métricas do Sistema</h3>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-surface-400">Total de Transações</span>
                <span className="text-white font-medium">{systemMetrics?.totalTransactions || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Volume Total</span>
                <span className="text-white font-medium">
                  R$ {((systemMetrics?.totalVolume || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Total de Taxas</span>
                <span className="text-white font-medium">
                  R$ {((systemMetrics?.totalFees || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Contas Ativas</span>
                <span className="text-white font-medium">{systemMetrics?.activeAccounts || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Providers Ativos</span>
                <span className="text-white font-medium">{systemMetrics?.activeProviders || 0}</span>
              </div>
            </div>
          </Card>
          <Card>
            <h3 className="text-lg font-medium text-white mb-4">Performance</h3>
            <div className="text-center py-8 text-surface-400">
              Gráficos de performance em breve
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default Monitoring;
